import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Layers, ArrowLeft, Eye, PartyPopper } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const QUALITY_BUTTONS = [
  { label: "Nochmal", quality: 1, className: "bg-error text-white" },
  { label: "Schwer", quality: 3, className: "bg-warning" },
  { label: "Gut", quality: 4, className: "bg-success" },
  { label: "Einfach", quality: 5, className: "bg-accent" },
];

export default function Flashcards() {
  const { refreshUser } = useAuth();
  const [decks, setDecks] = useState([]);
  const [session, setSession] = useState(null); // { deckTitle, queue: [], reviewed: 0 }
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadDecks = () => {
    api.get("/flashcards/decks").then((r) => setDecks(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadDecks();
  }, []);

  const startSession = async (deck) => {
    try {
      const { data } = await api.get("/flashcards/due");
      const queue = data.cards.filter((c) => c.deck_id === deck.id);
      setSession({ deckTitle: deck.title, queue, reviewed: 0, total: queue.length });
      setRevealed(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const review = async (quality) => {
    if (!session?.queue?.length) return;
    setSubmitting(true);
    const [current, ...rest] = session.queue;
    try {
      const { data } = await api.post(`/flashcards/${current.id}/review`, { quality });
      if (data.xp_awarded > 0) await refreshUser();
      setSession((prev) => ({ ...prev, queue: rest, reviewed: prev.reviewed + 1 }));
      setRevealed(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const endSession = () => {
    setSession(null);
    loadDecks();
  };

  if (session) {
    const current = session.queue[0];
    return (
      <div className="space-y-6" data-testid="flashcard-session">
        <button
          onClick={endSession}
          data-testid="flashcards-back"
          className="inline-flex items-center gap-1 font-bold text-sm hover:underline"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Decks
        </button>

        <div className="flex items-center justify-between">
          <h1 className="font-heading font-black text-2xl">{session.deckTitle}</h1>
          <span className="font-bold text-sm text-muted-foreground">
            {session.reviewed}/{session.total} wiederholt
          </span>
        </div>

        {!current ? (
          <div className="nb-card p-10 text-center space-y-3" data-testid="session-complete">
            <PartyPopper className="h-10 w-10 mx-auto text-primary" strokeWidth={2.5} />
            <h2 className="font-heading font-black text-2xl">Session abgeschlossen!</h2>
            <p className="text-muted-foreground font-medium">Du hast {session.reviewed} Karte(n) wiederholt.</p>
            <button
              onClick={endSession}
              data-testid="session-done-button"
              className="mt-2 bg-primary text-white font-bold px-5 py-2.5 rounded-lg border-2 border-black nb-shadow nb-press"
            >
              Zurück zu den Decks
            </button>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="nb-card p-8 sm:p-10 min-h-[220px] flex flex-col items-center justify-center text-center gap-4"
              data-testid="flashcard-face"
            >
              <p className="font-heading font-bold text-xl sm:text-2xl text-foreground">{current.front}</p>
              {current.code && (
                <pre className="w-full overflow-x-auto whitespace-pre-wrap rounded-xl border border-line bg-[#070A12] p-4 text-left font-mono text-sm text-foreground">
                  {current.code}
                </pre>
              )}
              {revealed && (
                <p className="w-full whitespace-pre-line border-t border-line pt-4 text-lg text-foreground" data-testid="flashcard-back">
                  {current.back}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        {current && !revealed && (
          <button
            onClick={() => setRevealed(true)}
            data-testid="flashcard-reveal"
            className="w-full flex items-center justify-center gap-2 bg-secondary text-white font-bold py-3 rounded-lg border-2 border-black nb-shadow nb-press"
          >
            <Eye className="h-4 w-4" strokeWidth={2.5} /> Antwort zeigen
          </button>
        )}

        {current && revealed && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {QUALITY_BUTTONS.map((b) => (
              <button
                key={b.quality}
                onClick={() => review(b.quality)}
                disabled={submitting}
                data-testid={`flashcard-quality-${b.quality}`}
                className={`font-bold text-sm py-3 rounded-lg border-2 border-black nb-shadow-sm nb-press disabled:opacity-60 ${b.className}`}
              >
                {b.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="flashcards-page">
      <div className="animate-fade-up">
        <div className="flex items-center gap-2">
          <Layers className="h-7 w-7 text-primary" strokeWidth={2.5} />
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Flashcards</h1>
        </div>
        <p className="text-muted-foreground font-medium mt-1">Wiederhole Konzepte mit Spaced Repetition (SM-2).</p>
      </div>

      {!decks.some((d) => d.personal) && (
        <p className="rounded-xl border border-secondary/40 bg-secondary/10 px-4 py-3 text-sm text-[#FDF2F8]">
          Übungen, die dir schwerfallen und die du danach löst, sammelt Bit hier als Deck <strong>„Deine Stolpersteine“</strong> zum Wiederholen.
        </p>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {decks.map((deck) => (
          <button
            key={deck.id}
            onClick={() => startSession(deck)}
            disabled={deck.due_count === 0}
            data-testid={`deck-card-${deck.id}`}
            className={`nb-card nb-press p-5 flex flex-col gap-3 text-left disabled:opacity-50 ${deck.personal ? "border-secondary/70 glow-bit" : ""}`}
          >
            {deck.personal && <p className="font-mono text-xs tracking-widest text-secondary">PERSÖNLICH · VON BIT</p>}
            <h3 className="font-heading font-bold text-lg text-foreground">{deck.title}</h3>
            <p className="text-sm text-muted-foreground">
              {deck.personal ? `${deck.card_count} Übung(en), die dir schwergefallen sind` : `${deck.card_count} Karten`}
            </p>
            {deck.due_count > 0 ? (
              <span className="self-start text-xs font-bold uppercase bg-warning border-2 border-black rounded-full px-2.5 py-0.5">
                {deck.due_count} fällig
              </span>
            ) : (
              <span className="self-start text-xs font-bold uppercase bg-muted border-2 border-black rounded-full px-2.5 py-0.5">
                Alles erledigt 🎉
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
